import { LIGHTER_CHAIN_ID } from '@funkit/chains'
import { useLighterAccountsQuery } from '@funkit/connect-core'
import type { Address } from 'viem'
import { isLighterxyzApiKey } from '../../utils/customers'

export interface LighterDepositRecipientOverride {
  /** True when this deposit targets Lighter — callers must not fall back to the L1 wallet address. */
  isLighterDeposit: boolean
  /** The L2 main-account index. Undefined while the lookup is pending, failed, or found no main account. */
  recipientOverride: string | undefined
  /** True while the account lookup is in flight — an unresolved recipient is expected then. */
  isLookupFetching: boolean
  /** Lookup query status, for the unresolved-recipient WARN log. */
  lookupStatus: 'pending' | 'error' | 'success'
}

/**
 * Lighter EOA generation targets the L2 main-account index, not the L1
 * address — resolves it as a deposit recipientAddr override (mirrors web's
 * `useLighterAccounts`). `isLighterDeposit` lets callers withhold the L1
 * wallet fallback until `recipientOverride` resolves, since the backend
 * rejects an L1 address for Lighter.
 */
export function useLighterDepositRecipientOverride({
  apiKey,
  targetChain,
  walletAddress,
}: {
  apiKey: string
  targetChain: string | undefined
  walletAddress: string | undefined
}): LighterDepositRecipientOverride {
  const isLighterDeposit =
    isLighterxyzApiKey(apiKey) && targetChain === LIGHTER_CHAIN_ID.toString()

  const { mainAccountIndex, isFetching, status } = useLighterAccountsQuery({
    address: isLighterDeposit ? (walletAddress as Address) : undefined,
  })

  return {
    isLighterDeposit,
    recipientOverride: mainAccountIndex,
    isLookupFetching: isFetching,
    lookupStatus: status,
  }
}
